import { LazyQueryExecFunction } from '@apollo/client';
import { GetHierarchyQuery, GetHierarchyQueryVariables } from '@modules/search';
import {
    ContentType,
    ContentTypeGroup,
    FieldSetting,
    ItemPositionType,
    NodeContent,
    NodeContentO,
    ObjectSchema,
} from '@src/__generated__/types';
import { InitialValue } from '@src/assets/types';
import { BaseMachineItemsRemoval, DependantGlsResponse } from '@src/hooks/cgt/useCgtHelpers';
import { getSourceFieldContent } from '@src/utils';
import {
    CheckGlItemsQuery,
    CheckGlItemsQueryVariables,
    CheckInvalidGlsQuery,
    CheckInvalidGlsQueryVariables,
} from '@src/utils/cgt/cgtQuery.generated';
import { GraphQLFormattedError } from 'graphql';
import { Dispatch, SetStateAction } from 'react';
import { IntlShape } from 'react-intl';

import {
    GetNodeCodeQuery,
    GetNodeCodeQueryVariables,
    PublishMutation,
} from '../Hierarchy.generated';
import {
    checkDependantGls,
    checkDuplicateGl,
    checkIfMandatoryFieldIsSet,
    checkIfRegexIsMatched,
    checkInvalidGls,
    checkRemovedBaseMachineItems,
    MappedValues,
} from './ValidateHelpers';

interface IIsPublishResponseError {
    errors?: readonly GraphQLFormattedError[];
    publishData?: PublishMutation | null;
}
export interface IProcessNodeData {
    objectSchema: ObjectSchema;
    contents: NodeContentWithInheritance[];
}

type NodeContentWithInheritance = NodeContent & { inherited: boolean };
export type CombinedSchemaAndContents = ReturnType<typeof processNodeData>;
export type CombinedInfo = ReturnType<typeof processNodeData>;
export type Paths = {
    path: string;
    valueContext: string;
    type: string;
    hierarchyId?: string | null;
}[];

function mergeBlueprintAndUserData(
    blueprint: ContentType[],
    userData: NodeContent[],
    groupCode: ContentTypeGroup['code'],
    repeatable: boolean,
) {
    const mergedData: Omit<NodeContent, 'value'>[] = [...userData];

    const uniqueIndices = [...new Set(userData.map(content => content.contentTypeGroupIndex))];

    uniqueIndices.forEach(index => {
        blueprint.forEach(field => {
            const fieldExists = mergedData.some(
                content =>
                    content.contentType === field.code && content.contentTypeGroupIndex === index,
            );

            if (!fieldExists) {
                mergedData.push({
                    contentType: field.code,
                    contentTypeGroup: groupCode,
                    contentTypeGroupIndex: index,
                    type: field.type,
                    repeatable,
                });
            }
        });
    });

    return mergedData;
}

function combineSchemaAndContents(
    contents: NodeContent[],
    groupCode: ContentTypeGroup['code'],
    contentTypes: ContentType[],
    repeatable: boolean,
) {
    const nodeContents = mergeBlueprintAndUserData(contentTypes, contents, groupCode, repeatable);

    return contentTypes.flatMap(schema => {
        const relatedContents = nodeContents.filter(content => content.contentType === schema.code);

        if (relatedContents.length > 0) {
            return relatedContents.map(content => ({
                ...schema,
                ...content,
                repeatable,
            }));
        }

        return {
            ...schema,
            contentTypeGroupIndex: 0,
            contentTypeGroup: groupCode,
            contentType: schema.code,
            repeatable,
        };
    });
}

export function isPublishResponseError({ errors, publishData }: IIsPublishResponseError): boolean {
    const { publish } = publishData || {};
    const { success } = publish || {};

    return Boolean(errors || (publish && success !== true));
}

export function buildErrorMessage(
    errors?: readonly GraphQLFormattedError[],
    message?: string,
): string {
    if (errors) {
        return errors.reduce((acc, err) => acc + err.message, '');
    }
    return message || 'UNKNOWN_PUBLISH_ERROR';
}

export function processNodeData({ objectSchema, contents }: IProcessNodeData) {
    if (!objectSchema || !objectSchema.contentTypeGroups) {
        return [];
    }

    return objectSchema.contentTypeGroups.flatMap(contentTypeGroup => {
        const nodeContents = contents.filter(
            content => content.contentTypeGroup === contentTypeGroup.code,
        );

        if (!contentTypeGroup.contentTypes) {
            return [];
        }

        return combineSchemaAndContents(
            nodeContents,
            contentTypeGroup.code,
            contentTypeGroup.contentTypes,
            contentTypeGroup.repeatable,
        );
    });
}

export function getPathToValue(values: InitialValue | null | undefined): Paths {
    return Object.entries(values ?? {})
        .map(([key, value]) => {
            const fields = value?.[0];
            if (!fields) return [];
            const contexts = Object.entries(fields)?.map(([key2, value2]) => {
                const { valueContext } = getSourceFieldContent(`${key}.0`, value2);
                const path = `${key}-${key2}`;
                return { path, valueContext, type: value2.type, hierarchyId: value2.hierarchy };
            });
            return [contexts];
        })
        .flat(2);
}

export async function validateFields({
    initialValues,
    editNodeId,
    editNodeDialogId,
    dirtyFields,
    hierarchyId,
    checkGLItems,
    fetchInvalidGls,
    glHierarchyId,
    cgtModelHierarchyId,
    priceListHierarchyId,
    glItemsField,
    nodeContents,
    getCodes,
    company,
    setInvalidGls,
    setDependantResponse,
    fetchNodesWithContents,
    setBaseMachineItemsRemoval,
    skipBaseMachineItemsRemovalCheck,
    formValues,
    formDirtyFields,
    intl,
    contentTypeGroups,
    currentDepth,
}: {
    initialValues: InitialValue;
    editNodeId: string;
    editNodeDialogId?: string;
    dirtyFields: NodeContent[];
    hierarchyId: string;
    checkGLItems: LazyQueryExecFunction<CheckGlItemsQuery, CheckGlItemsQueryVariables>;
    fetchInvalidGls?: LazyQueryExecFunction<CheckInvalidGlsQuery, CheckInvalidGlsQueryVariables>;
    glHierarchyId?: string;
    cgtModelHierarchyId?: string;
    priceListHierarchyId?: string;
    glItemsField?: FieldSetting;
    nodeContents: NodeContentO[];
    getCodes: LazyQueryExecFunction<GetNodeCodeQuery, GetNodeCodeQueryVariables>;
    company?: string;
    setInvalidGls: Dispatch<SetStateAction<({ id: string; code: string } | null)[] | null>>;
    setDependantResponse: Dispatch<SetStateAction<DependantGlsResponse | null | undefined>>;
    fetchNodesWithContents: LazyQueryExecFunction<GetHierarchyQuery, GetHierarchyQueryVariables>;
    setBaseMachineItemsRemoval: Dispatch<SetStateAction<BaseMachineItemsRemoval | null>>;
    skipBaseMachineItemsRemovalCheck: boolean;
    formValues: InitialValue;
    formDirtyFields: InitialValue;
    intl: IntlShape;
    contentTypeGroups?: ObjectSchema['contentTypeGroups'];
    currentDepth?: number;
}): Promise<{
    message?: string;
    shouldReturn: boolean;
    keepDirtyState?: boolean;
    // Fields that failed the client-side checks; the channel filter warns when one is hidden.
    invalidFields?: { contentTypeGroup: string; contentType: string }[];
}> {
    const mappedFields = Object.keys(initialValues).flatMap(group =>
        Object.values(initialValues[group]).flatMap(ct =>
            Object.values(ct).reduce<MappedValues[]>((acc, value) => {
                // handle select with multiValue
                let contentValue = value.value;
                if (value.type === 'select' && value.multiLanguage) {
                    const [firstValue] = contentValue;
                    // override value for
                    contentValue = contentValue.map(item => ({
                        ...item,
                        value: firstValue.value,
                    }));
                }

                acc.push({
                    contentType: value.contentType,
                    contentTypeGroup: value.contentTypeGroup,
                    contentTypeGroupIndex: value.contentTypeGroupIndex,
                    value: contentValue,
                    mandatoryForSave: value.mandatoryForSave,
                    regex: value.regex,
                    dynamicRegex: value.dynamicRegex,
                    multiLanguage: !!value.multiLanguage,
                    multiValue: !!value.multiValue,
                    type: value.type,
                    name: value.name,
                });

                return acc;
            }, []),
        ),
    );

    // updated values of mappedFields
    const updatedFields = mappedFields.map(field => {
        const dirtyField = dirtyFields.find(
            val =>
                val.contentTypeGroup === field.contentTypeGroup &&
                val.contentTypeGroupIndex === field.contentTypeGroupIndex &&
                val.contentType === field.contentType,
        );
        if (dirtyField) return { ...field, value: dirtyField.value };
        return field;
    });

    const invalidMultipleValues = updatedFields.filter(
        field =>
            !field.multiValue && field.type !== 'dateRange' && field.value?.[0]?.value?.length > 1,
    );

    if (invalidMultipleValues.length) {
        return {
            message: intl.formatMessage(
                {
                    id: 'fields-with-multiple-values',
                    defaultMessage:
                        'These fields can not have multiple values: {invalidMultipleValues}',
                },
                {
                    invalidMultipleValues: invalidMultipleValues
                        .map(
                            field =>
                                `${
                                    field.name?.[0]?.value ??
                                    `${field.contentTypeGroup}-${field.contentType}`
                                }`,
                        )
                        .join(', '),
                },
            ),
            shouldReturn: true,
            invalidFields: invalidMultipleValues.map(({ contentTypeGroup, contentType }) => ({
                contentTypeGroup,
                contentType,
            })),
        };
    }

    // filter out fields from content type groups not visible at the current depth
    const depthVisibleFields = updatedFields.filter(field => {
        if (currentDepth == null || !contentTypeGroups) return true;
        const ctg = contentTypeGroups.find(g => g.code === field.contentTypeGroup);
        return !ctg?.showLevel || Number(ctg.showLevel) === currentDepth;
    });

    // mandatoryForSave fields that don't have a value
    const mandatoryFieldsNotSet = depthVisibleFields.filter(field =>
        checkIfMandatoryFieldIsSet(field, depthVisibleFields),
    );

    if (mandatoryFieldsNotSet.length) {
        return {
            message: intl.formatMessage(
                {
                    id: 'missing-mandatory-fields-for-save',
                    defaultMessage: 'Missing mandatory fields for save: {mandatoryFieldsNotSet}',
                },
                {
                    mandatoryFieldsNotSet: mandatoryFieldsNotSet
                        .map(
                            field =>
                                `${
                                    field.name?.[0]?.value ??
                                    `${field.contentTypeGroup}-${field.contentType}`
                                }`,
                        )
                        .join(', '),
                },
            ),
            shouldReturn: true,
            invalidFields: mandatoryFieldsNotSet.map(({ contentTypeGroup, contentType }) => ({
                contentTypeGroup,
                contentType,
            })),
        };
    }

    // fields with regex that is not matching
    const fieldsUnmatchingRegex = depthVisibleFields.filter(field =>
        checkIfRegexIsMatched(field, depthVisibleFields),
    );

    if (fieldsUnmatchingRegex.length) {
        return {
            message: intl.formatMessage(
                {
                    id: 'fields-not-matching-regex',
                    defaultMessage:
                        "The following fields don't match the defined regex: {fieldsUnmatchingRegex}",
                },
                {
                    fieldsUnmatchingRegex: fieldsUnmatchingRegex
                        .map(
                            field =>
                                `${
                                    field.name?.[0]?.value ??
                                    `${field.contentTypeGroup}-${field.contentType}`
                                }`,
                        )
                        .join(', '),
                },
            ),
            shouldReturn: true,
            invalidFields: fieldsUnmatchingRegex.map(({ contentTypeGroup, contentType }) => ({
                contentTypeGroup,
                contentType,
            })),
        };
    }

    // asyncronous valdiity checks
    const duplicateGlCode = await checkDuplicateGl({
        hierarchyId,
        modelId: editNodeDialogId,
        updatedFields,
        dirtyFields,
        checkGLItems,
        glHierarchyId,
        glItemsField,
        company,
        nodeId: editNodeId,
    });

    if (duplicateGlCode && !duplicateGlCode?.success) {
        const message = duplicateGlCode?.codes?.length
            ? `${duplicateGlCode.message}: ${duplicateGlCode.codes.join(', ')}`
            : duplicateGlCode.message;
        return {
            message,
            shouldReturn: true,
        };
    }

    const invalidGls = await checkInvalidGls({
        hierarchyId,
        updatedFields,
        cgtModelHierarchyId,
        fetchInvalidGls,
        modelId: editNodeId,
        nodeContents,
        company,
    });

    if (invalidGls?.length) {
        setInvalidGls(invalidGls ?? []);
        return {
            shouldReturn: true,
        };
    }

    const dependantResponse = await checkDependantGls({
        nodeContents,
        dirtyFields,
        hierarchyId,
        cgtModelHierarchyId,
        getCodes,
        glHierarchyId,
        priceListHierarchyId,
        company,
    });

    if (dependantResponse) {
        setDependantResponse(dependantResponse);
        return {
            shouldReturn: true,
        };
    }

    // Removing the base machine item from a ZMMB gl needs an explicit confirmation
    if (!skipBaseMachineItemsRemovalCheck) {
        const removedBaseMachineItems = await checkRemovedBaseMachineItems({
            hierarchyId,
            glHierarchyId,
            priceListHierarchyId,
            glItemsField,
            updatedFields,
            dirtyFields,
            nodeContents,
            fetchNodesWithContents,
        });

        if (removedBaseMachineItems.length) {
            setBaseMachineItemsRemoval({
                items: removedBaseMachineItems,
                formValues,
                formDirtyFields,
            });
            // keep the form dirty: cancelling the confirmation must leave the pending changes
            // saveable, and the dialog replays the save from its own snapshot anyway
            return {
                shouldReturn: true,
                keepDirtyState: true,
            };
        }
    }

    // Check merchandise items
    const itemsField = updatedFields.find(f => f.contentType === 'items');
    const merchandiseItems =
        itemsField?.value[0].value.filter(v => v?.positionType === ItemPositionType.Merchandise) ??
        [];
    if (merchandiseItems.length > 1) {
        return {
            message: 'Seleziona solo un item Merchandise',
            shouldReturn: true,
        };
    }

    // Check Energy rilevante_per_gcm content for ZEAT gl
    if (company === 'cgt-en') {
        const glType = updatedFields.find(f => f.contentType === 'type')?.value[0].value[0];
        const rilevantePerGcm = updatedFields.find(f => f.contentType === 'rilevante_per_gcm')
            ?.value[0].value[0];
        if (glType === 'ZEAT' && !rilevantePerGcm) {
            return {
                message:
                    'Non è possibile rimuovere il campo Rilevante per GCM per i GL di tipo ZEAT',
                shouldReturn: true,
            };
        }
    }

    // Check GL MM Dispo
    const mmDispo = dirtyFields.find(f => f.contentType === 'mm_dispo');
    if (hierarchyId === glHierarchyId && mmDispo) {
        const mmDispoValue = mmDispo?.value[0]?.value[0];
        const isMissingPosition =
            mmDispoValue &&
            (typeof mmDispoValue === 'string' || !('position' in (mmDispoValue ?? {})));

        if (isMissingPosition) {
            return {
                message: 'Il campo posizione è obbligatorio per MM Dispo',
                shouldReturn: true,
            };
        }

        const position = Number(mmDispoValue?.position?.trim());

        if (mmDispoValue && (Number.isNaN(position) || position < 1 || position > 5)) {
            return {
                message: 'Il campo posizione deve essere un numero tra 1 e 5 per MM Dispo',
                shouldReturn: true,
            };
        }
    }

    return {
        shouldReturn: false,
    };
}
